import React, { useRef, useState } from "react";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Upload, Trash2, ArrowUp, ArrowDown, Plus, Image as ImageIcon,
  Camera, Video, Heart, Smile, Aperture, Film, Plane, Wand2, Scissors,
  BookOpen, Radio, Lamp, Lightbulb, Move, Sparkles, Clapperboard, Users,
  Star, ShieldCheck, MapPin, LayoutGrid, PartyPopper, Briefcase, Bird,
  Mountain, UtensilsCrossed, Baby, Home as HomeIcon, Package, User,
} from "@/lib/icons";

const ICON_MAP = {
  Camera, Video, Heart, Smile, Aperture, Film, Plane, Wand2, Scissors,
  BookOpen, Radio, Lamp, Lightbulb, Move, Sparkles, Clapperboard, Users,
  Star, ShieldCheck, MapPin, LayoutGrid, PartyPopper, Briefcase, Bird,
  Mountain, UtensilsCrossed, Baby, Home: HomeIcon, Package, User,
};
const ICON_OPTIONS = Object.keys(ICON_MAP);

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 5;

export default function CategoryManager({ value = [], onChange }) {
  const items = Array.isArray(value) ? value : [];

  const updateAt = (idx, patch) => {
    const clone = items.slice();
    clone[idx] = { ...clone[idx], ...patch };
    onChange?.(clone);
  };
  const removeAt = (idx) => {
    if (!window.confirm("Delete this category?")) return;
    onChange?.(items.filter((_, i) => i !== idx));
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const clone = items.slice();
    [clone[idx], clone[j]] = [clone[j], clone[idx]];
    onChange?.(clone);
  };
  const addNew = () => {
    onChange?.([
      ...items,
      { name: "New category", icon: "Camera", icon_url: "", enabled: true, order: items.length + 1 },
    ]);
  };

  return (
    <div className="space-y-2.5">
      {items.map((c, i) => (
        <CategoryRow
          key={i}
          index={i}
          cat={c}
          isFirst={i === 0}
          isLast={i === items.length - 1}
          onChange={(patch) => updateAt(i, patch)}
          onRemove={() => removeAt(i)}
          onMove={(dir) => move(i, dir)}
        />
      ))}
      <button
        type="button"
        onClick={addNew}
        data-testid="cms-category-add"
        className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white mt-2"
      >
        <Plus size={14} /> Add category
      </button>
    </div>
  );
}

function CategoryRow({ index, cat, isFirst, isLast, onChange, onRemove, onMove }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const enabled = cat.enabled !== false;
  const Icon = ICON_MAP[cat.icon] || Camera;
  const uploaded = resolvePhotoUrl(cat.icon_url);

  const doUpload = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type.toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Icon must be ${MAX_MB} MB or less`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/site/category-icon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ icon_url: data.icon_url });
      toast.success("Icon uploaded");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className={`glass rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3 transition-opacity ${enabled ? "" : "opacity-60"}`}
      data-testid={`cms-category-row-${index}`}
    >
      <span className="text-xs font-mono text-white/40 w-6 text-center shrink-0">{index + 1}</span>

      {/* Icon preview */}
      <div className="relative h-12 w-12 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
        {uploaded ? (
          <img src={uploaded} alt="" className="w-full h-full object-contain p-1.5" />
        ) : (
          <Icon size={22} className="text-fuchsia-300" />
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center text-white text-[10px]">
            …
          </div>
        )}
      </div>

      {/* Name */}
      <Input
        data-testid={`cms-category-name-${index}`}
        className="flex-1 h-10 bg-white/5 border-white/10 text-white"
        value={cat.name || ""}
        onChange={(e) => onChange({ name: e.target.value })}
      />

      {/* Icon dropdown */}
      <select
        data-testid={`cms-category-icon-${index}`}
        className="h-10 w-36 bg-white/5 border border-white/10 rounded-md text-white text-xs px-2"
        value={cat.icon || "Camera"}
        onChange={(e) => onChange({ icon: e.target.value })}
        title="Choose a Lucide icon"
      >
        {ICON_OPTIONS.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>

      {/* Upload icon */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => doUpload(e.target.files?.[0])}
        data-testid={`cms-category-icon-input-${index}`}
      />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`cms-category-icon-upload-${index}`}
          className="btn-ghost inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-60"
          title={uploaded ? "Replace uploaded icon" : "Upload custom icon"}
        >
          {uploaded ? <ImageIcon size={11} /> : <Upload size={11} />}
          {uploaded ? "Replace" : "Upload"}
        </button>
        {uploaded && (
          <button
            type="button"
            onClick={() => onChange({ icon_url: "" })}
            data-testid={`cms-category-icon-clear-${index}`}
            className="text-[10px] text-white/50 hover:text-red-300 px-1"
            title="Use Lucide icon instead"
          >
            clear
          </button>
        )}
      </div>

      {/* Enable */}
      <div className="flex items-center gap-2">
        <Switch
          data-testid={`cms-category-enabled-${index}`}
          checked={enabled}
          onCheckedChange={(v) => onChange({ enabled: v })}
        />
        <span className="text-[11px] text-white/60 w-14">{enabled ? "Visible" : "Hidden"}</span>
      </div>

      {/* Reorder + delete */}
      <div className="flex items-center gap-1">
        <IconBtn
          testid={`cms-cat-up-${index}`}
          disabled={isFirst}
          onClick={() => onMove(-1)}
          Icon={ArrowUp}
        />
        <IconBtn
          testid={`cms-cat-down-${index}`}
          disabled={isLast}
          onClick={() => onMove(1)}
          Icon={ArrowDown}
        />
        <IconBtn
          testid={`cms-cat-del-${index}`}
          tone="red"
          onClick={onRemove}
          Icon={Trash2}
        />
      </div>
    </div>
  );
}

function IconBtn({ Icon, testid, onClick, disabled, tone }) {
  const color =
    tone === "red"
      ? "text-red-400 hover:bg-red-500/15"
      : "text-white/70 hover:text-fuchsia-300 hover:bg-white/5";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded-lg disabled:opacity-30 ${color}`}
    >
      <Icon size={13} />
    </button>
  );
}
